import axios from "axios";
import { cookieUtils, getTenantNameForHeader } from "../utils/cookieUtils";
import { clearTokenRefreshTimer } from "../utils/tokenRefresh";
import { clearAdminCache } from "../utils/userRoles";
import { logToServer } from "../utils/logs";
import { isPublicAuthPage } from "../utils/authSessionUtils";
import {
  getSessionAccessDeniedMessage,
  isSessionAccessDenied,
} from "../utils/sessionAccessUtils";
import { publishTransportFailure } from "../errors/apiErrorBus";
import { isGithubApiUrl } from "../errors/classifyApiError";
import { DEFAULT_API_TIMEOUT_MS } from "../errors/constants";

import { CONFIG } from "../config.js";

const BASE_URL = CONFIG.REACT_APP_BACKEND_URL || "http://localhost:8000/api";

const CREDENTIAL_AUTH_PATHS = [
  "/auth/login",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/super-admin/login",
];

/** True when API signals Host/JWT tenant binding failed. */
function isTenantMismatchError(error) {
  const detail = error?.response?.data?.detail;
  return (
    typeof detail === "string" &&
    detail.trim().toLowerCase() === "tenant mismatch"
  );
}

// Create axios instance — default timeout so hung servers surface as errors.
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: DEFAULT_API_TIMEOUT_MS,
});

// Request interceptor to add tenant-name header from sessionStorage
axiosInstance.interceptors.request.use(
  (config) => {
    const tenantName = getTenantNameForHeader();
    if (tenantName) {
      config.headers["tenant-name"] = tenantName;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Global flag to prevent multiple simultaneous logouts
let isLoggingOut = false;

// True only after /auth/me succeeds in this page load. A 401 on the first
// check (closed tab, came back the next day) is a quiet return to sign-in.
let sessionLiveThisPage = false;

// Called by App.js after login so a future session expiry can be handled again
export const resetLogoutState = () => {
  isLoggingOut = false;
};

/**
 * Clear the signed-in session after the API rejects it.
 * The warning toast is shown only when this page already had a live session,
 * so opening the app after the 24-hour cookie ends does not look like an error.
 */
export const handleAuthSessionExpired = ({ forceNotify = false } = {}) => {
  if (isLoggingOut) {
    return;
  }

  const shouldNotify =
    sessionLiveThisPage && (forceNotify || !isPublicAuthPage());

  sessionLiveThisPage = false;
  isLoggingOut = true;
  if (shouldNotify) {
    console.warn("Session expired. Logging out...");
  }

  clearTokenRefreshTimer();
  cookieUtils.clearAuthCookies();
  localStorage.removeItem("user");
  localStorage.removeItem("userRole");

  axios
    .post(`${BASE_URL}/auth/clear-session`, {}, { withCredentials: true })
    .catch(() => null);

  window.dispatchEvent(
    new CustomEvent("auth-session-expired", {
      detail: { notify: shouldNotify },
    }),
  );
};

const LIMIT_EXPIRED_CODE = "LIMIT_EXPIRED";
const LIMIT_EXPIRED_MESSAGE =
  "limit expired, please re-subscribe to continue";

function isLimitExpiredPayload(data) {
  return (
    data &&
    typeof data === "object" &&
    data.success === false &&
    data.code === LIMIT_EXPIRED_CODE
  );
}

function dispatchLimitExpired(detail) {
  window.dispatchEvent(
    new CustomEvent("token-exhausted", {
      detail: {
        message: detail?.message || LIMIT_EXPIRED_MESSAGE,
        code: LIMIT_EXPIRED_CODE,
        status: detail?.status ?? 200,
      },
    }),
  );
}

axiosInstance.interceptors.response.use(
  (response) => {
    const requestUrl = response.config?.url || "";
    if (requestUrl.includes("/auth/me")) {
      sessionLiveThisPage = true;
    }
    if (isLimitExpiredPayload(response?.data)) {
      dispatchLimitExpired({
        message: response.data.message,
        status: response.status,
      });
      const error = new Error(response.data.message || LIMIT_EXPIRED_MESSAGE);
      error.response = response;
      error.isLimitExpired = true;
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || "";
    const isCredentialAuth = CREDENTIAL_AUTH_PATHS.some((p) =>
      requestUrl.includes(p),
    );
    const logoutRequired =
      error.response?.data?.logout_required ||
      error.response?.headers?.["x-logout-required"] === "true";

    if (!error.response) {
      console.error("Network Error", error);
      if (!isGithubApiUrl(requestUrl)) {
        publishTransportFailure(error, { source: "axios-network" });
      }
      return Promise.reject(error);
    }

    const tenantMismatch = isTenantMismatchError(error);

    // Tenant mismatch: drop local session; API also expires HttpOnly app_session.
    // Skip forceNotify on login itself so the form can show the API error.
    if (tenantMismatch && (status === 401 || status === 403)) {
      logToServer("WARNING", "API request returned Tenant mismatch", {
        url: requestUrl,
        method: error.config?.method,
        status,
        tenant_name: getTenantNameForHeader(),
        user_id: cookieUtils.getAuthValue("userid"),
      });
      handleAuthSessionExpired({
        forceNotify: !isCredentialAuth && !isPublicAuthPage(),
      });
      return Promise.reject(error);
    }

    if (status === 401 && !isCredentialAuth) {
      logToServer("WARNING", "API request returned 401", {
        url: requestUrl,
        method: error.config?.method,
        status,
        tenant_name: getTenantNameForHeader(),
        user_id: cookieUtils.getAuthValue("userid"),
      });

      if (!isPublicAuthPage()) {
        handleAuthSessionExpired();
      }
      return Promise.reject(error);
    }

    if (status === 402 || isLimitExpiredPayload(error.response?.data)) {
      dispatchLimitExpired({
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          LIMIT_EXPIRED_MESSAGE,
        status: status || 200,
      });
      return Promise.reject(error);
    }

    if (status === 403) {
      // Public pages (landing/marketing, invite links, etc.) have no session to
      // expire and no "/home" to redirect to — just surface the error to the
      // caller instead of hijacking navigation away from whatever page this is.
      if (isPublicAuthPage() || isGithubApiUrl(requestUrl)) {
        return Promise.reject(error);
      }

      if (logoutRequired) {
        handleAuthSessionExpired({ forceNotify: true });
        return Promise.reject(error);
      }

      if (isSessionAccessDenied(error)) {
        window.dispatchEvent(
          new CustomEvent("session-access-denied", {
            detail: {
              message: getSessionAccessDeniedMessage(error),
            },
          }),
        );
        return Promise.reject(error);
      }

      console.warn("Access forbidden. Redirecting to home...");

      if (window.location.pathname !== "/home") {
        window.location.href = "/home";
      }
      return Promise.reject(error);
    }

    // Transport / service failures (timeout, 408, 429, 5xx) open support.
    // GitHub failures stay on the GitHub screen as a toast, never as 502/504 UI.
    if (!isGithubApiUrl(requestUrl)) {
      publishTransportFailure(error, { source: "axios" });
    }

    return Promise.reject(error);
  },
);

/**
 * Auth Service
 */
export const loginUser = async ({ email, password }) => {
  const res = await axiosInstance.post(
    "/auth/login",
    { email, password },
    { withCredentials: true },
  );

  if (res.data?.user) {
    cookieUtils.saveAuthData(res.data.user);
  }

  return res.data;
};

export const forgotPassword = async ({ email }) => {
  const res = await axiosInstance.post("/auth/forgot-password", { email });
  return res.data;
};

export const resetPassword = async ({
  email,
  confirmationCode,
  newPassword,
}) => {
  const res = await axiosInstance.post("/auth/reset-password", {
    email,
    confirmation_code: confirmationCode,
    new_password: newPassword,
  });
  return res.data;
};

const notifyAuthCacheChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("nova_auth_changed", {
      detail: { user: cookieUtils.loadAuthData() },
    }),
  );
};

export const updateOwnProfile = async ({ firstName, lastName, phoneNumber }) => {
  const res = await axiosInstance.put("/auth/update-profile", {
    firstname: firstName,
    lastname: lastName,
    phonenumber: phoneNumber,
  });
  if (res.data?.user) {
    cookieUtils.setAuthValue("firstname", res.data.user.firstname);
    cookieUtils.setAuthValue("lastname", res.data.user.lastname);
    cookieUtils.setAuthValue(
      "username",
      [res.data.user.firstname, res.data.user.lastname].filter(Boolean).join(" "),
    );
    cookieUtils.setAuthValue("phonenumber", res.data.user.phonenumber);
    notifyAuthCacheChanged();
  }
  return res.data;
};

export const changeOwnPassword = async ({ currentPassword, newPassword }) => {
  const res = await axiosInstance.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res.data;
};

export const uploadOwnProfilePicture = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axiosInstance.post("/auth/profile-picture", formData);
  if (res.data?.profilepic) {
    cookieUtils.setAuthValue("profilepic", res.data.profilepic);
    notifyAuthCacheChanged();
  }
  return res.data;
};

export const removeOwnProfilePicture = async () => {
  const res = await axiosInstance.delete("/auth/profile-picture");
  cookieUtils.setAuthValue("profilepic", null);
  notifyAuthCacheChanged();
  return res.data;
};

export const logoutUser = async () => {
  try {
    const res = await axiosInstance.post("/auth/logout");

    if (res.data.success) {
      console.log(res.data.message);
    }

    return res.data;
  } finally {
    clearTokenRefreshTimer();
    clearAdminCache();
    cookieUtils.clearAuthCookies();
  }
};

/**
 * Sessions Service
 */
export const getUserSessions = async ({ page = 1, pageSize = 20 } = {}) => {
  const res = await axiosInstance.post("/get-user-sessions", {
    page,
    page_size: pageSize,
  });
  return res.data;
};

export const renameSession = async (id, sessionname) => {
  const res = await axiosInstance.post("/rename-session", { id, sessionname });
  return res.data;
};

export const deleteSession = async (id) => {
  const res = await axiosInstance.delete("/delete-session", {
    data: { session_id: id },
  });
  return res.data;
};

export const createNewSession = async (userId, sessionname) => {
  const res = await axiosInstance.post("/create-session", {
    userid: Number(userId),
    sessionname,
  });
  return res.data;
};

/**
 * Chat Service (example placeholder)
 */
export const getChatMessages = async (sessionId) => {
  const res = await axiosInstance.get(`/get-messages/${sessionId}`);
  return res.data.messages;
};
export const getChatHistory = async (sessionId) => {
  const res = await axiosInstance.post("/get-chat-history", { id: sessionId });
  return res.data.chat;
};

export const sendMessage = async (sessionId, message) => {
  const res = await axiosInstance.post("/send-message", { sessionId, message });
  return res.data;
};

/** Admin Service */

export const getActiveEmployees = async () => {
  try {
    const response = await axiosInstance.get("/get-active-employee-data");
    return response.data;
  } catch (error) {
    console.error("Error fetching active employees:", error);
    throw error;
  }
};

export const getAllEmployees = async () => {
  try {
    const response = await axiosInstance.get("/get-all-employee-data");
    return response.data;
  } catch (error) {
    console.error("Error fetching all employees:", error);
    throw error;
  }
};

/** Files Upload Services */
export const uploadEmployeeData = async (file) => {
  if (!file) {
    throw new Error("No file provided for upload");
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await axiosInstance.post("/users/bulk-upload", file, {
      headers: {},
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("❌ Backend responded with error:", error.response.data);
      throw new Error(
        error.response.data.message || "Server error during upload",
      );
    } else if (error.request) {
      console.error("❌ No response received:", error.request);
      throw new Error("No response from server");
    } else {
      console.error("❌ Upload error:", error.message);
      throw new Error("Upload failed");
    }
  }
};

/** GitHub Integration Service — OAuth-based; the access token lives server-side, never in the client */

export const fetchGithubStatus = async () => {
  const response = await axiosInstance.get("/github/status");
  return response.data;
};

export const startGithubOAuth = async (sessionId) => {
  const response = await axiosInstance.get("/github/oauth/authorize", {
    params: sessionId ? { session_id: sessionId } : undefined,
  });
  return response.data;
};

export const GITHUB_STATUS_CHANGED_EVENT = "nova-github-status";
export const GITHUB_SESSIONS_CHANGED_EVENT = "nova-github-sessions";

export function isGithubAuthFailure(error) {
  const detail = error?.response?.data?.detail;
  const text = [detail, error?.message].filter(Boolean).join(" ");
  if (
    error?.response?.status === 409 &&
    /github connection expired|connect your github/i.test(text)
  ) {
    return true;
  }
  return /bad credentials|invalid token/i.test(text);
}

export function notifyGithubStatusChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(GITHUB_STATUS_CHANGED_EVENT));
}

export const disconnectGithub = async () => {
  const response = await axiosInstance.post("/github/disconnect");
  return response.data;
};

export const fetchAuthenticatedUser = async () => {
  const response = await axiosInstance.post("/github/user");
  if (response.status !== 200) throw new Error("Invalid token");
  return response.data;
};

export const fetchRepositories = async () => {
  const response = await axiosInstance.post("/github/repos");
  if (response.status !== 200) throw new Error("Failed to fetch repositories");
  return response.data;
};

/**
 * Fetch branches for a repo.
 * - No page/query: flat list (legacy, first 200 branches).
 * - page: { branches, next_page } with 100 branches per page; page 1 pins the default branch.
 * - query: { branches, next_page } holding the exact-name match, if any.
 */
export const fetchBranches = async (owner, repo, page, query) => {
  const response = await axiosInstance.post("/github/branches", {
    owner,
    repo,
    ...(page ? { page } : {}),
    ...(query ? { query } : {}),
  });
  if (response.status !== 200) throw new Error("Failed to fetch branches");
  return response.data;
};

export const fetchRepoContents = async (owner, repo, path = "", ref) => {
  // Encode each path segment to handle special characters, but keep '/' as separator
  const encodedPath = path
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  try {
    const response = await axiosInstance.post("/github/contents", {
      owner: encodeURIComponent(owner),
      repo: encodeURIComponent(repo),
      path: encodedPath,
      ref: ref ? encodeURIComponent(ref) : "",
    });
    if (response.status !== 200) {
      throw new Error(
        response.data?.detail || "Failed to fetch repository contents",
      );
    }
    return response.data;
  } catch (error) {
    const detail =
      error.response?.data?.detail ||
      error.message ||
      "Failed to fetch repository contents";
    throw new Error(detail);
  }
};

export const fetchFileRawContent = async (apiUrl) => {
  const response = await axiosInstance.post("/github/raw-file", {
    api_url: apiUrl,
  });
  if (response.status !== 200) throw new Error("Failed to fetch file content");
  return response.data;
};

export const fetchIssues = async (owner, repo) => {
  const response = await axiosInstance.post("/github/issues", {
    owner: encodeURIComponent(owner),
    repo: encodeURIComponent(repo),
  });
  if (response.status !== 200) throw new Error("Failed to fetch the issues");
  return response.data;
};

/** GitHub repo Q&A — persisted per user, resumable across reloads */

export const startGithubSession = async (owner, repo, ref, question) => {
  const response = await axiosInstance.post("/github/start-session-with-message", {
    owner,
    repo,
    ref,
    question,
  });
  if (response.status !== 200) throw new Error("Failed to start the repo chat");
  return response.data;
};

export const addGithubMessage = async (sessionId, question) => {
  const response = await axiosInstance.post("/github/add-message", {
    session_id: sessionId,
    question,
  });
  if (response.status !== 200) throw new Error("Failed to send the message");
  return response.data;
};

export const fetchGithubSessions = async () => {
  const response = await axiosInstance.get("/github/get-user-sessions");
  return response.data?.sessions || [];
};

export const deleteGithubSession = async (sessionId) => {
  const response = await axiosInstance.post("/github/delete-session", {
    session_id: sessionId,
  });
  return response.data;
};

export const fetchGithubSessionMessages = async (sessionId) => {
  const response = await axiosInstance.post("/github/get-chat-history", {
    session_id: sessionId,
  });
  return response.data;
};

export default axiosInstance;
